import { httpsCallable } from "firebase/functions";
import { onAuthStateChanged, User } from "firebase/auth";
import {
  FunctionComponent,
  PropsWithChildren,
  useState,
  createContext,
  useContext,
  useLayoutEffect,
} from "react";
import { doc, getDoc } from "firebase/firestore";
import { Auth, Firestore, Functions } from "../firebase/firebase";

export type UserProps = {
  uid: string;
  managers: Record<string, boolean>;
  email?: string | null;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

type OkRes = { status: "OK"; response: UserProps; domain?: string };
type ErrRes = { status: "Error"; message?: string; code?: number };
type Res = OkRes | ErrRes;

function cleanUpUser(rawUser: UserProps) {
  return {
    ...rawUser,
    managers: Object.entries(rawUser.managers || {}).reduce(
      (out, [managerID, access]) =>
        access ? { ...out, [managerID]: access } : out,
      {} as UserProps["managers"]
    ),
  };
}

export async function callGetUser(): Promise<UserProps> {
  const user = Auth.currentUser;
  if (!user) throw new Error("Ikke innlogget");

  const fn = httpsCallable(Functions, "call-admin-campaign-getUser");
  const res = (await fn({})).data as Res;

  console.log(res);

  if (res.status === "OK") {
    const u = res.response || ({} as UserProps);
    return cleanUpUser(u);
  }

  throw new Error(res.message || `Feil (${res.code ?? "ukjent"})`);
}

async function getUserFirestore(firebaseUser: User) {
  const userSnap = await getDoc(doc(Firestore, "users", firebaseUser.uid));
  const rawUser = userSnap.data() as UserProps | undefined;

  if (!rawUser) {
    return null;
  }
  return cleanUpUser(rawUser);
}

type ModeOption = "firestore" | "call";
const MODE: ModeOption = "firestore";

type UserContextData = {
  authUser: User | null | undefined;
  userData: UserProps | null | undefined;
};
const UserContext = createContext<UserContextData | undefined>(undefined);

const UserContextProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const [authUser, setAuthUser] = useState<User | null>();
  const [userData, setUserData] = useState<UserProps | undefined | null>(
    undefined
  );
  const [loaded, setLoaded] = useState(false);

  useLayoutEffect(() => {
    return onAuthStateChanged(Auth, async (firebaseUser) => {
      setLoaded(true);
      setAuthUser(firebaseUser);
      if (!firebaseUser) {
        setUserData(null);
        return;
      }

      switch (MODE as ModeOption) {
        case "firestore": {
          return getUserFirestore(firebaseUser)
            .then((u) => setUserData(u))
            .catch(() => setUserData(null));
        }
        case "call":
        default: {
          return callGetUser()
            .then((u) => setUserData(u))
            .catch(() => setUserData(null));
        }
      }
    });
  }, []);

  if (!loaded) {
    return null;
  }

  return (
    <UserContext.Provider value={{ authUser, userData }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContextProvider;

export function useUserContext() {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("Missing provider");
  }

  return context;
}
